/*
 * ============================================================
 * AKAY AI TOKEN SYNC — SHOPIFY EVENTS + SERVER POLLING
 * ============================================================
 *
 * SHOPIFY EVENTS KURULUMUNDA YAPTIKLARIMIZ:
 *
 * 1) Terminalden Shopify CLI ile AKAY Backend app'e bağlandık.
 *
 * 2) shopify.app.toml içine Shopify Events subscription ekledik:
 *    - Topic: Customer
 *    - Action: update
 *    - Trigger:
 *      customer.metafield(
 *        namespace: '$app',
 *        key: 'ai_tokens'
 *      ).value
 *
 * 3) shopify app deploy --no-release ile version oluşturduk,
 *    kontrol ettikten sonra version'ı release ettik.
 *
 * 4) Shopify'daki ai_tokens değişince:
 *
 *      Shopify Event
 *          ↓
 *      shopifyAiTokenEvent
 *
 *    Firebase HTTP function'ı tetikleniyor.
 *
 * 5) shopifyAiTokenEvent:
 *    - Shopify Event HMAC'ini doğruluyor.
 *    - customer ID + yeni aiTokens değerini alıyor.
 *    - Firestore'a server-side mirror ediyor:
 *
 *      ShopifyCustomerRealtimeStates/
 *      shopify_<customerNumericId>
 *
 * 6) İlk realtime çözümünde browser'dan Firestore
 *    onSnapshot() kullanmayı denedik.
 *
 *    Fakat projede Cloud Firestore App Check ENFORCED
 *    olduğu için Shopify storefront browser'ından doğrudan
 *    Firestore listener açmak yerine güvenlik yapısını
 *    değiştirmeden server polling modeline geçtik.
 *
 * 7) createShopifyRealtimeSession function adı
 *    deployment/index.js uyumluluğu için AYNI bırakıldı.
 *
 *    Artık custom Firebase auth session oluşturmak yerine:
 *
 *      Shopify customer context
 *          ↓
 *      customerProofSignature doğrulaması
 *          ↓
 *      Firestore server-side mirror
 *          ↓
 *      gerekiyorsa Shopify Admin API fallback
 *          ↓
 *      aiTokens
 *
 *    döndürür.
 *
 * 8) BU DOSYA:
 *    - Liquid'deki ilk ai_tokens değerini gösterir.
 *    - Server'dan güncel token değerini alır.
 *    - Sayfa açıkken periyodik olarak kontrol eder.
 *    - Sekmeye geri dönüldüğünde hemen kontrol eder.
 *    - Sayfadaki bütün
 *      [data-akay-ai-token-balance]
 *      alanlarını birlikte günceller.
 *
 * ÖNEMLİ:
 *
 * - Frontend'deki değer sadece UI içindir.
 * - Gerçek token yetkisi/kontrolü backend + Shopify'dadır.
 * - Browser Firestore'a doğrudan erişmez.
 * - Firebase Web SDK gerekmez.
 * - Firebase public config gerekmez.
 * - Firestore App Check ENFORCED olarak kalır.
 * - Bu JS içine hiçbir SECRET koyulmaz.
 *
 * ============================================================
 */

(function () {
  "use strict";

  if (window.AKAYAITokens) {
    return;
  }

  // =========================================================
  // CONSTANTS
  // =========================================================

  const FUNCTIONS_BASE =
    "https://europe-west1-butikleapp.cloudfunctions.net";

  /*
   * Function adı eski deployment/index.js ile uyumluluk için
   * createShopifyRealtimeSession olarak bırakıldı.
   *
   * Artık Firebase Custom Token oluşturmaz.
   * Güvenli şekilde AI token bakiyesi döndürür.
   */
  const BALANCE_FUNCTION =
    `${FUNCTIONS_BASE}/createShopifyRealtimeSession`;

  /*
   * Sayfa açık ve görünür durumdaysa
   * bu aralıkla server'dan bakiye kontrol edilir.
   */
  const POLL_INTERVAL_MS = 30000;

  /*
 * Virtual Try gibi aynı browser'daki başka bir sayfa
 * token değişikliğini bildirirse açık Account sekmelerine
 * anında refresh sinyali gönderilir.
 */
const BALANCE_CHANGED_STORAGE_KEY =
  "akay_ai_token_balance_changed";

  /*
   * focus + visibilitychange gibi eventlerin aynı anda
   * gereksiz çift request oluşturmasını engeller.
   */
  const MIN_REFRESH_GAP_MS =
    3000;

  // =========================================================
  // STATE
  // =========================================================

  let lastForcedRefreshAt = 0;

  const FORCED_REFRESH_DEDUPE_MS = 2000;

  let currentValue =
    null;

  let pollTimer =
    null;

  let pollingStarted =
    false;

  let refreshInFlight =
    false;

  let lastRefreshAt =
    0;

  // =========================================================
  // TOKEN VALUE
  // =========================================================

  function normalize(value) {
    const parsed =
      Number.parseInt(
        String(value ?? ""),
        10
      );

    if (
      !Number.isInteger(parsed) ||
      parsed < 0
    ) {
      return null;
    }

    return parsed;
  }

  function render(value) {
    document
      .querySelectorAll(
        "[data-akay-ai-token-balance]"
      )
      .forEach((element) => {
        element.textContent =
          String(value);

        element.dataset.akayAiTokenValue =
          String(value);
      });
  }

  function set(
    value,
    source = "unknown"
  ) {
    const normalized =
      normalize(value);

    if (normalized === null) {
      return false;
    }

    const previousValue =
      currentValue;

    currentValue =
      normalized;

    render(normalized);

    /*
     * Diğer AKAY JS'leri isterse bu event'i dinleyebilir.
     *
     * Örnek:
     *
     * window.addEventListener(
     *   "akay:ai-tokens-changed",
     *   ...
     * );
     */
    window.dispatchEvent(
      new CustomEvent(
        "akay:ai-tokens-changed",
        {
          detail: {
            value:
              normalized,

            previousValue,

            source,
          },
        }
      )
    );

    return true;
  }

  function get() {
    return currentValue;
  }

  function refresh() {
    if (currentValue === null) {
      return;
    }

    render(currentValue);
  }

  function hasBalanceElement() {
    return Boolean(
      document.querySelector(
        "[data-akay-ai-token-balance]"
      )
    );
  }

  // =========================================================
  // LIQUID INITIAL VALUE
  // =========================================================

  function initializeFromLiquid() {
    const element =
      document.querySelector(
        "[data-akay-ai-token-balance]"
      );

    if (!element) {
      return;
    }

    const initialValue =
      element.dataset
        .akayAiTokenInitial ||
      element.textContent;

    set(
      initialValue,
      "liquid"
    );
  }

  // =========================================================
  // SHOPIFY CUSTOMER CONTEXT
  // =========================================================

  function getShopifyContext() {
    const context =
      window
        .AKAYShopifyCustomerContext ||
      {};

    return {
      source:
        String(
          context.source || ""
        )
          .trim()
          .toLowerCase(),

      shopDomain:
        String(
          context.shopDomain || ""
        )
          .trim()
          .toLowerCase(),

      customerNumericId:
        String(
          context.customerNumericId ||
            ""
        ).trim(),

      customerProofSignature:
        String(
          context.customerProofSignature ||
            ""
        ).trim(),
    };
  }

  function hasValidShopifyContext(
    context
  ) {
    return Boolean(
      context &&
      context.source === "shopify" &&
      context.shopDomain &&
      /^\d+$/.test(
        context.customerNumericId
      ) &&
      context.customerProofSignature
    );
  }

  // =========================================================
  // SERVER BALANCE REQUEST
  // =========================================================

 async function fetchBalanceFromServer(
  context,
  forceFresh = false
) {
    const response =
      await fetch(
        BALANCE_FUNCTION,
        {
          method: "POST",

          mode: "cors",

          credentials: "omit",

          headers: {
            "Content-Type":
              "application/json",
          },

          /*
           * Firebase https.onCall endpoint formatı.
           */
          body: JSON.stringify({
            data: {
              source: context.source,
              shopDomain: context.shopDomain,
              customerNumericId: context.customerNumericId,
              customerProofSignature: context.customerProofSignature,

              /*
              * true ise Firestore mirror kullanılmadan
              * Shopify'daki gerçek ai_tokens tekrar okunur.
              */
              forceFresh: forceFresh === true,
            },
          }),
        }
      );

    let payload =
      null;

    try {
      payload =
        await response.json();
    } catch (_) {
      throw new Error(
        "ai-token-balance-invalid-response"
      );
    }

    if (
      !response.ok ||
      payload?.error
    ) {
      const message =
        payload?.error?.message ||
        payload?.error?.status ||
        "ai-token-balance-request-failed";

      throw new Error(
        message
      );
    }

    const aiTokens =
      normalize(
        payload?.result
          ?.aiTokens
      );

    if (aiTokens === null) {
      throw new Error(
        "ai-token-balance-missing"
      );
    }

    return {
      aiTokens,

      source:
        String(
          payload?.result
            ?.source ||
            "server"
        ),
    };
  }

  // =========================================================
  // REFRESH FROM SERVER
  // =========================================================

  async function refreshFromServer(
    source = "server-refresh",
    force = false,
    forceFresh = false
  ) {
    /*
     * Token gösterecek hiçbir element yoksa
     * gereksiz function çağrısı yapma.
     */
    if (!hasBalanceElement()) {
      return false;
    }

    const context =
      getShopifyContext();

    /*
     * Shopify customer login değilse
     * hiçbir server request yapılmaz.
     */
    if (
      !hasValidShopifyContext(
        context
      )
    ) {
      return false;
    }

    if (refreshInFlight) {
      return false;
    }

    if (forceFresh) {
      const forcedNow = Date.now();

      if (
        forcedNow - lastForcedRefreshAt <
        FORCED_REFRESH_DEDUPE_MS
      ) {
        return false;
      }

      lastForcedRefreshAt = forcedNow;
    }

    const now =
      Date.now();

    if (
      !force &&
      now - lastRefreshAt <
        MIN_REFRESH_GAP_MS
    ) {
      return false;
    }

    refreshInFlight =
      true;

    lastRefreshAt =
      now;

    try {
      const result = await fetchBalanceFromServer(
          context,
          forceFresh
        );

      set(
        result.aiTokens,
        source
      );

      /*
      console.log(
        "[AKAY_AI_TOKENS] balance_updated",
        {
          aiTokens:
            result.aiTokens,

          balanceSource:
            result.source,

          updateSource:
            source,
        }
      );*/

      return true;
    } catch (error) {
      /*
       * Server geçici olarak ulaşılamazsa
       * Liquid'den veya son başarılı request'ten
       * aldığımız mevcut değeri koruyoruz.
       */
      /*
      console.error(
        "[AKAY_AI_TOKENS] balance_refresh_failed",
        {
          message:
            error?.message || "",
        }
      );*/

      return false;
    } finally {
      refreshInFlight =
        false;
    }
  }

  // =========================================================
// TOKEN CHANGE NOTIFICATION
// =========================================================

/*
 * Virtual Try / Video gibi token bakiyesini değiştirebilecek
 * başarılı bir işlemden sonra çağrılır.
 *
 * Aynı sayfada balance elementi varsa:
 * → Shopify'daki güncel değer hemen tekrar okunur.
 *
 * Başka Account sekmesi açıksa:
 * → localStorage "storage" eventi ile o sekme de
 *   anında güncel değeri tekrar okur.
 *
 * Response içindeki tahmini/kalan değer kullanılmaz.
 * Backend'den güncel değer tekrar alınır.
 */
function notifyBalanceChanged(
  source = "external-token-change"
) {
  try {
    localStorage.setItem(
      BALANCE_CHANGED_STORAGE_KEY,
      JSON.stringify({
        source,
        at: Date.now(),
      })
    );
  } catch (_) {}

  /*
   * Bu sayfada AI token göstergesi varsa
   * aynı sayfayı da hemen güncelle.
   *
   * true, true:
   * - debounce'u bekleme
   * - Firestore mirror yerine güncel Shopify değerini al
   */
  if (hasBalanceElement()) {
    return refreshFromServer(
      source,
      true,
      true
    );
  }

  return Promise.resolve(false);
}

  // =========================================================
  // POLLING
  // =========================================================

  async function startPolling() {
    if (pollingStarted) {
      return;
    }

    if (!hasBalanceElement()) {
      return;
    }

    const context =
      getShopifyContext();

    if (
      !hasValidShopifyContext(
        context
      )
    ) {
      return;
    }

    pollingStarted =
      true;

    /*
     * İlk açılışta Liquid değerini beklemeden
     * server ile hemen doğrula.
     */
    await refreshFromServer(
      "server-initial",
      true
    );

    pollTimer =
      window.setInterval(
        function () {
          /*
           * Arka plandaki sekmede gereksiz
           * function çağrısı yapma.
           */
          if (document.hidden) {
            return;
          }

          refreshFromServer(
            "server-poll"
          );
        },
        POLL_INTERVAL_MS
      );

      /*
    console.log(
      "[AKAY_AI_TOKENS] polling_started",
      {
        intervalMs:
          POLL_INTERVAL_MS,

        customerNumericId:
          context.customerNumericId,
      }
    );
    */
  }

  function stopPolling() {
    if (pollTimer) {
      window.clearInterval(
        pollTimer
      );
    }

    pollTimer =
      null;

    pollingStarted =
      false;
  }

  // =========================================================
  // PUBLIC API
  // =========================================================

  window.AKAYAITokens = {
  set,
  get,
  refresh,

  refreshFromServer,
  notifyBalanceChanged,

  startPolling,
  stopPolling,
};

  // =========================================================
  // BOOT
  // =========================================================

  function boot() {
    /*
     * 1) Önce Shopify Liquid'in server-side verdiği
     *    başlangıç bakiyesini göster.
     */
    initializeFromLiquid();

    /*
     * 2) Sonra backend üzerinden güncel değeri doğrula
     *    ve polling'i başlat.
     */
    startPolling();
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true,
      }
    );
  } else {
    boot();
  }

  // =========================================================
  // PAGE / TAB EVENTS
  // =========================================================

  /*
 * Aynı browser'daki başka bir AKAY sekmesinde
 * token değiştiren bir işlem tamamlanırsa
 * Account sekmesi 30 saniyelik polling'i beklemez.
 */
window.addEventListener(
  "storage",
  function (event) {
    if (
      event.key !==
        BALANCE_CHANGED_STORAGE_KEY ||
      !event.newValue
    ) {
      return;
    }

    refreshFromServer(
      "cross-tab-token-change",
      true,
      true
    );
  }
);

  /*
   * Kullanıcı başka sekmeye gidip geri gelirse
   * interval'i beklemeden hemen bakiye kontrol edilir.
   */
  window.addEventListener(
    "focus",
    function () {
      refreshFromServer(
        "window-focus"
      );
    }
  );

  document.addEventListener(
    "visibilitychange",
    function () {
      if (document.hidden) {
        return;
      }

      refreshFromServer(
        "visibility-change"
      );
    }
  );

  /*
   * Shopify Theme Editor veya sonradan yüklenen section
   * yeni balance elementi eklerse mevcut değeri bas.
   */
  document.addEventListener(
    "shopify:section:load",
    function () {
      refresh();

      if (!pollingStarted) {
        startPolling();
      }
    }
  );

  /*
   * Sayfadan çıkarken interval'i temizle.
   */
  window.addEventListener(
    "pagehide",
    function () {
      stopPolling();
    }
  );
})();